// ============================================================
//  src/pages/community/CommunityPage.js
//  Styles: ./CommunityPage.css (scoped under .cmy-*)
// ============================================================
import React, {
  useEffect, useState, useCallback, useRef, useContext, createContext,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listCommunityPosts, getCommunityPost,
  createCommunityPost, deleteCommunityPost, resolveCommunityPost,
  likeCommunityPost, createCommunityReply,
  acceptCommunityReply, deleteCommunityReply, getBillingOverview,
} from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import './CommunityPage.css';

// ── helpers ───────────────────────────────────────────────────
function timeAgo(str) {
  if (!str) return '';
  const s = Math.floor((Date.now() - new Date(str.replace(' ','T'))) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s/86400)}d ago`;
  return new Date(str.replace(' ','T')).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function initials(name='') {
  return name.split(' ').map(p=>p[0]??'').join('').toUpperCase().slice(0,2) || '?';
}
const AV_COLORS = ['#0E9F6E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#4F46E5', '#059669'];
function avColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
const IMG_BASE = process.env.REACT_APP_API_URL?.replace('/api','') ?? '';
const n = v => parseInt(v ?? 0, 10) || 0;

function Avatar({ name, size = 'md' }) {
  return <span className={`cmy-av ${size}`} style={{ background: avColor(name) }} aria-hidden="true">{initials(name)}</span>;
}

function StatusPill({ status }) {
  return status === 'resolved'
    ? <span className="cmy-pill is-resolved"><i className="fa-solid fa-circle-check" /> Resolved</span>
    : <span className="cmy-pill is-open"><span className="cmy-pill-dot" /> Open</span>;
}

// ── Context (share current user sId down) ────────────────────
const MeCtx = createContext(null);

// ============================================================
//  IMAGE ANNOTATOR
// ============================================================
function ImageAnnotator({ file, onDone, onCancel }) {
  const canvasRef  = useRef(null);
  const [arrows,   setArrows]   = useState([]);   // [{x1,y1,x2,y2}]
  const [drawing,  setDrawing]  = useState(null); // {x1,y1}
  const [imgEl,    setImgEl]    = useState(null);
  const [color,    setColor]    = useState('#ef4444');
  const COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#a855f7','#ffffff'];

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      const cvs = canvasRef.current;
      if (!cvs) return;
      const maxW = Math.min(img.width,  800);
      const maxH = Math.min(img.height, 520);
      const ratio = Math.min(maxW/img.width, maxH/img.height, 1);
      cvs.width  = Math.round(img.width  * ratio);
      cvs.height = Math.round(img.height * ratio);
      redraw(img, [], cvs);
    };
    img.src = URL.createObjectURL(file);
    return () => URL.revokeObjectURL(img.src);
  // eslint-disable-next-line
  }, [file]);

  function redraw(img, arrs, cvs) {
    const c = (cvs ?? canvasRef.current);
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    arrs.forEach(a => drawArrow(ctx, a.x1, a.y1, a.x2, a.y2, a.color));
  }

  function drawArrow(ctx, x1,y1,x2,y2,col='#ef4444') {
    const headLen = 18;
    const angle   = Math.atan2(y2-y1, x2-x1);
    ctx.save();
    ctx.strokeStyle = col;
    ctx.fillStyle   = col;
    ctx.lineWidth   = 3;
    ctx.lineCap     = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen*Math.cos(angle-0.4), y2 - headLen*Math.sin(angle-0.4));
    ctx.lineTo(x2 - headLen*Math.cos(angle+0.4), y2 - headLen*Math.sin(angle+0.4));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  function getPos(e) {
    const cvs  = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    const scaleX = cvs.width  / rect.width;
    const scaleY = cvs.height / rect.height;
    const src = e.touches ? (e.touches[0] || e.changedTouches[0]) : e;
    return {
      x: (src.clientX - rect.left)  * scaleX,
      y: (src.clientY - rect.top)   * scaleY,
    };
  }

  function onDown(e) {
    e.preventDefault();
    const p = getPos(e);
    setDrawing({ x1: p.x, y1: p.y });
  }

  function onMove(e) {
    e.preventDefault();
    if (!drawing || !imgEl) return;
    const p = getPos(e);
    const arrs = [...arrows, { ...drawing, x2: p.x, y2: p.y, color }];
    redraw(imgEl, arrs);
  }

  function onUp(e) {
    e.preventDefault();
    if (!drawing || !imgEl) return;
    const p = getPos(e);
    const newArr = { ...drawing, x2: p.x, y2: p.y, color };
    const arrs = [...arrows, newArr];
    setArrows(arrs);
    setDrawing(null);
    redraw(imgEl, arrs);
  }

  function undo() {
    const arrs = arrows.slice(0, -1);
    setArrows(arrs);
    if (imgEl) redraw(imgEl, arrs);
  }

  function clearAll() {
    setArrows([]);
    if (imgEl) redraw(imgEl, []);
  }

  function handleDone() {
    canvasRef.current.toBlob(blob => {
      const annotated = new File([blob], 'annotated.png', { type: 'image/png' });
      onDone(annotated);
    }, 'image/png');
  }

  return (
    <div className="cmy-annot">
      <div className="cmy-annot-bar">
        <span className="cmy-annot-label"><i className="fa-solid fa-arrow-pointer" /> Arrow colour</span>
        <div className="cmy-swatches">
          {COLORS.map(c => (
            <button key={c} type="button" className={`cmy-swatch ${color === c ? 'is-active' : ''}`}
              style={{ background: c }} onClick={() => setColor(c)} aria-label={`Colour ${c}`} />
          ))}
        </div>
        <div className="cmy-annot-tools">
          <button type="button" className="cmy-btn ghost sm" onClick={undo} disabled={arrows.length === 0}>
            <i className="fa-solid fa-rotate-left" /> Undo
          </button>
          <button type="button" className="cmy-btn ghost sm" onClick={clearAll} disabled={arrows.length === 0}>
            Clear
          </button>
        </div>
      </div>
      <div className="cmy-canvas-wrap">
        <canvas
          ref={canvasRef}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        />
      </div>
      <p className="cmy-annot-hint"><i className="fa-solid fa-hand-pointer" /> Click and drag on the image to draw arrows pointing at the problem.</p>
      <div className="cmy-modal-actions">
        <button type="button" className="cmy-btn ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="cmy-btn primary" onClick={handleDone}><i className="fa-solid fa-check" /> Use this image</button>
      </div>
    </div>
  );
}

// ============================================================
//  NEW POST MODAL
// ============================================================
function NewPostModal({ onClose, onPosted }) {
  const [title,      setTitle]      = useState('');
  const [body,       setBody]       = useState('');
  const [rawFile,    setRawFile]    = useState(null);   // original upload
  const [finalFile,  setFinalFile]  = useState(null);   // annotated result
  const [annotating, setAnnotating] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [dragOver,   setDragOver]   = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose, saving]);

  const takeFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setRawFile(f);
    setAnnotating(true);
    setFinalFile(null);
    setPreview(null);
  };
  const handleFile = (e) => takeFile(e.target.files[0]);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); takeFile(e.dataTransfer.files?.[0]); };

  const onAnnotationDone = (annotated) => {
    setFinalFile(annotated);
    setAnnotating(false);
    setPreview(URL.createObjectURL(annotated));
  };

  const removeImage = () => {
    setRawFile(null); setFinalFile(null); setPreview(null); setAnnotating(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!body.trim())  { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('action', 'create');
      fd.append('title',  title.trim());
      fd.append('body',   body.trim());
      if (finalFile) fd.append('image', finalFile, 'annotated.png');
      const res = await createCommunityPost(fd);
      if (res.data?.success) {
        toast.success('Post created!');
        onPosted(res.data.pId);
      } else {
        toast.error(res.data?.message ?? 'Failed to create post');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cmy-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className={`cmy-modal ${annotating ? 'is-wide' : ''}`} onClick={e=>e.stopPropagation()} role="dialog" aria-label="New community post">
        <span className="cmy-modal-grip" />
        <div className="cmy-modal-head">
          <span className="cmy-modal-ic"><i className={`fa-solid ${annotating ? 'fa-pen-ruler' : 'fa-pen-to-square'}`} /></span>
          <div>
            <h3>{annotating ? 'Highlight the problem' : 'Ask the community'}</h3>
            <p>{annotating ? 'Draw arrows so helpers see exactly what you mean.' : 'Clear questions get faster, better answers.'}</p>
          </div>
          <button type="button" className="cmy-icon-btn" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>

        {annotating ? (
          <ImageAnnotator
            file={rawFile}
            onDone={onAnnotationDone}
            onCancel={() => { setAnnotating(false); setRawFile(null); if(fileRef.current) fileRef.current.value=''; }}
          />
        ) : (
          <div className="cmy-form">
            <div className="cmy-field">
              <div className="cmy-label-row">
                <label htmlFor="cmy-title">Title</label>
                <span className={title.length > 180 ? 'is-warn' : ''}>{title.length}/200</span>
              </div>
              <input
                id="cmy-title"
                className="cmy-input"
                placeholder="e.g. WhatsApp bot not responding after deploy"
                value={title}
                onChange={e=>setTitle(e.target.value)}
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="cmy-field">
              <div className="cmy-label-row"><label htmlFor="cmy-body">Details</label></div>
              <textarea
                id="cmy-body"
                className="cmy-input"
                rows={6}
                placeholder="What were you trying to do? What did you expect, and what happened instead? Paste any error message."
                value={body}
                onChange={e=>setBody(e.target.value)}
              />
            </div>

            <div className="cmy-field">
              <div className="cmy-label-row"><label>Screenshot</label><span>Optional</span></div>
              {preview ? (
                <div className="cmy-preview">
                  <img src={preview} alt="Annotated screenshot" />
                  <div className="cmy-preview-actions">
                    <button type="button" className="cmy-btn ghost sm" onClick={() => setAnnotating(true)}><i className="fa-solid fa-pen" /> Re-annotate</button>
                    <button type="button" className="cmy-btn ghost sm danger" onClick={removeImage}><i className="fa-regular fa-trash-can" /> Remove</button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={`cmy-drop ${dragOver ? 'is-over' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                >
                  <span className="cmy-drop-ic"><i className="fa-regular fa-image" /></span>
                  <b>Drop a screenshot or click to upload</b>
                  <span>You can draw arrows to highlight the issue</span>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
            </div>

            <div className="cmy-modal-actions">
              <button type="button" className="cmy-btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className="cmy-btn primary" onClick={handleSubmit} disabled={saving}>
                {saving ? <><span className="cmy-spin" /> Posting…</> : <><i className="fa-solid fa-paper-plane" /> Post question</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  POST DETAIL VIEW
// ============================================================
function PostDetail({ pId, onBack, canPost = true }) {
  const me = useContext(MeCtx);
  const { user } = useAuth();
  const myName = `${user?.sFname ?? ''} ${user?.sLname ?? ''}`.trim() || 'You';
  const navigate = useNavigate();
  const [post,    setPost]    = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [liked,   setLiked]   = useState(false);
  const [likes,   setLikes]   = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getCommunityPost(pId);
      if (res.data?.success) {
        setPost(res.data.post);
        setReplies(res.data.replies ?? []);
        setLiked(!!res.data.post.pLiked);
        setLikes(parseInt(res.data.post.pLikeCount ?? 0));
      }
    } catch { toast.error('Failed to load post'); }
    setLoading(false);
  }, [pId]);

  useEffect(() => { load(); window.scrollTo({ top: 0 }); }, [load]);

  const handleLike = async () => {
    // optimistic
    setLiked(l => !l);
    setLikes(c => c + (liked ? -1 : 1));
    try {
      const res = await likeCommunityPost(pId);
      if (res.data?.success) {
        setLiked(res.data.liked);
        setLikes(res.data.count);
      }
    } catch { load(); }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await createCommunityReply(pId, reply.trim());
      if (res.data?.success) {
        toast.success('Reply posted');
        setReply('');
        load();
      }
    } catch { toast.error('Failed to post reply'); }
    setSending(false);
  };

  const handleAccept = async (rId) => {
    try {
      await acceptCommunityReply(rId, pId);
      toast.success('Marked as solution');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteReply = async (rId) => {
    try {
      await deleteCommunityReply(rId);
      setReplies(prev => prev.filter(r => r.rId !== rId));
      toast.success('Reply deleted');
    } catch { toast.error('Failed'); }
  };

  const handleResolve = async () => {
    try {
      await resolveCommunityPost(pId);
      setPost(p => ({ ...p, pStatus: 'resolved' }));
      toast.success('Marked as resolved');
    } catch {}
  };

  const handleDeletePost = async () => {
    try {
      await deleteCommunityPost(pId);
      toast.success('Post deleted');
      onBack();
    } catch { toast.error('Failed'); }
  };

  if (loading) {
    return (
      <div className="cmy-detail">
        <div className="cmy-card cmy-skel"><span className="sk w30" /><span className="sk w70 h24" /><span className="sk w90" /><span className="sk w80" /></div>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="cmy-detail">
        <button type="button" className="cmy-back" onClick={onBack}><i className="fa-solid fa-arrow-left" /> Community</button>
        <div className="cmy-empty"><span className="cmy-empty-ic"><i className="fa-regular fa-comment-dots" /></span><b>Post not found</b><span>It may have been deleted.</span></div>
      </div>
    );
  }

  const isAuthor = parseInt(post.pSId) === parseInt(me);
  const accepted = replies.filter(r => r.rIsAccepted);
  const ordered  = [...accepted, ...replies.filter(r => !r.rIsAccepted)];

  return (
    <div className="cmy-detail">
      <button type="button" className="cmy-back" onClick={onBack}><i className="fa-solid fa-arrow-left" /> Community</button>

      <article className="cmy-card cmy-post">
        <div className="cmy-post-head">
          <Avatar name={post.pAuthorName} />
          <div className="cmy-post-who">
            <b>{post.pAuthorName}{isAuthor && <em className="cmy-you">You</em>}</b>
            <span>{timeAgo(post.pCreatedAt)} · {n(post.pViews).toLocaleString()} views</span>
          </div>
          <StatusPill status={post.pStatus} />
        </div>

        <h1 className="cmy-post-title">{post.pTitle}</h1>
        <p className="cmy-post-body">{post.pBody}</p>

        {post.pImagePath && (
          <button type="button" className="cmy-post-img" onClick={() => window.open(`${IMG_BASE}/${post.pImagePath}`, '_blank')} title="Open full size">
            <img src={`${IMG_BASE}/${post.pImagePath}`} alt="Screenshot attached to this post" />
            <span><i className="fa-solid fa-up-right-and-down-left-from-center" /> Full size</span>
          </button>
        )}

        <div className="cmy-post-actions">
          <button type="button" className={`cmy-like ${liked ? 'is-liked' : ''}`} onClick={handleLike} aria-pressed={liked}>
            <i className={`fa-${liked ? 'solid' : 'regular'} fa-heart`} /> {likes} <span>{likes === 1 ? 'like' : 'likes'}</span>
          </button>
          <span className="cmy-post-stat"><i className="fa-regular fa-comment" /> {replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>

          {isAuthor && (
            <div className="cmy-owner-actions">
              {post.pStatus === 'open' && (
                <button type="button" className="cmy-btn ghost sm ok" onClick={handleResolve}>
                  <i className="fa-solid fa-check" /> Mark resolved
                </button>
              )}
              {!confirmDelete ? (
                <button type="button" className="cmy-btn ghost sm danger" onClick={() => setConfirmDelete(true)} aria-label="Delete post">
                  <i className="fa-regular fa-trash-can" /> <span>Delete</span>
                </button>
              ) : (
                <span className="cmy-inline-confirm">
                  Delete this post?
                  <button type="button" className="cmy-btn sm danger-solid" onClick={handleDeletePost}>Delete</button>
                  <button type="button" className="cmy-btn ghost sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </span>
              )}
            </div>
          )}
        </div>
      </article>

      <div className="cmy-replies-head">
        <h2>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</h2>
        {post.pStatus === 'resolved' && accepted.length > 0 && <span className="cmy-solved-note"><i className="fa-solid fa-circle-check" /> Solved</span>}
      </div>

      {replies.length === 0 ? (
        <div className="cmy-empty sm">
          <span className="cmy-empty-ic"><i className="fa-regular fa-comments" /></span>
          <b>No replies yet</b>
          <span>Know the answer? Be the first to help.</span>
        </div>
      ) : (
        <div className="cmy-thread">
          {ordered.map(r => (
            <div key={r.rId} className={`cmy-reply ${r.rIsAccepted ? 'is-accepted' : ''}`}>
              {r.rIsAccepted ? <div className="cmy-accepted"><i className="fa-solid fa-circle-check" /> Accepted solution</div> : null}
              <div className="cmy-reply-head">
                <Avatar name={r.rAuthorName} size="sm" />
                <div className="cmy-post-who">
                  <b>{r.rAuthorName}{parseInt(r.rSId) === parseInt(post.pSId) && <em className="cmy-op">Author</em>}</b>
                  <span>{timeAgo(r.rCreatedAt)}</span>
                </div>
                <div className="cmy-reply-tools">
                  {isAuthor && !r.rIsAccepted && post.pStatus === 'open' && (
                    <button type="button" className="cmy-btn ghost sm ok" onClick={() => handleAccept(r.rId)}>
                      <i className="fa-solid fa-check" /> <span>Accept</span>
                    </button>
                  )}
                  {parseInt(r.rSId) === parseInt(me) && (
                    <button type="button" className="cmy-icon-btn danger" title="Delete reply" aria-label="Delete reply" onClick={() => handleDeleteReply(r.rId)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  )}
                </div>
              </div>
              <p className="cmy-reply-body">{r.rBody}</p>
            </div>
          ))}
        </div>
      )}

      <div className="cmy-card cmy-composer">
        {canPost ? (
          <>
            <div className="cmy-composer-head">
              <Avatar name={myName} size="sm" />
              <b>Add a reply</b>
            </div>
            <textarea
              className="cmy-input"
              rows={4}
              placeholder="Share a solution, a tip, or ask a follow-up question…"
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleReply(); }}
            />
            <div className="cmy-composer-foot">
              <span className="cmy-kbd-hint">Ctrl + Enter to post</span>
              <button type="button" className="cmy-btn primary" onClick={handleReply} disabled={sending || !reply.trim()}>
                {sending ? <><span className="cmy-spin" /> Posting…</> : <><i className="fa-solid fa-reply" /> Post reply</>}
              </button>
            </div>
          </>
        ) : (
          <div className="cmy-locked">
            <span className="cmy-locked-ic"><i className="fa-solid fa-lock" /></span>
            <div>
              <b>Replying needs Community access</b>
              <span>Upgrade your plan to answer questions and post your own.</span>
            </div>
            <button type="button" className="cmy-btn dark" onClick={() => navigate('/billing')}>Upgrade</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  POST CARD (list view)
// ============================================================
function PostCard({ post, onClick }) {
  const body = post.pBody ?? '';
  return (
    <article
      className={`cmy-item ${post.pStatus === 'resolved' ? 'is-resolved' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onClick(); }}
    >
      <div className="cmy-item-main">
        <div className="cmy-item-head">
          <Avatar name={post.pAuthorName} size="sm" />
          <span className="cmy-item-author">{post.pAuthorName}</span>
          <span className="cmy-dot" />
          <span className="cmy-item-time">{timeAgo(post.pCreatedAt)}</span>
          <StatusPill status={post.pStatus} />
        </div>
        <h3 className="cmy-item-title">{post.pTitle}</h3>
        <p className="cmy-item-body">{body}</p>
        <div className="cmy-item-foot">
          <span className={n(post.pReplyCount) ? 'has' : ''}><i className="fa-regular fa-comment" /> {n(post.pReplyCount)} {n(post.pReplyCount) === 1 ? 'reply' : 'replies'}</span>
          <span><i className="fa-regular fa-heart" /> {n(post.pLikeCount)}</span>
          <span><i className="fa-regular fa-eye" /> {n(post.pViews).toLocaleString()}</span>
          {post.pImagePath && <span className="cmy-item-has-img"><i className="fa-regular fa-image" /> Screenshot</span>}
        </div>
      </div>
      {post.pImagePath && (
        <div className="cmy-item-thumb">
          <img src={`${IMG_BASE}/${post.pImagePath}`} alt="" loading="lazy" />
        </div>
      )}
    </article>
  );
}

// ============================================================
//  MAIN PAGE
// ============================================================
const FILTERS = [
  { key: 'latest',  label: 'Latest',  icon: 'fa-clock' },
  { key: 'popular', label: 'Popular', icon: 'fa-fire' },
  { key: 'open',    label: 'Unanswered', icon: 'fa-circle-question' },
];

export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view,      setView]      = useState('list');
  const [posts,     setPosts]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState('latest');
  const [showNew,   setShowNew]   = useState(false);
  const [canPost,   setCanPost]   = useState(true);
  const LIMIT = 20;
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (off=0, fil=filter) => {
    setLoading(true);
    try {
      const [postsRes, billingRes] = await Promise.allSettled([
        listCommunityPosts(LIMIT, off, fil),
        getBillingOverview(),
      ]);
      if (postsRes.status === 'fulfilled' && postsRes.value?.data?.success) {
        setPosts(postsRes.value.data.posts ?? []);
        setTotal(postsRes.value.data.total ?? 0);
      }
      if (billingRes.status === 'fulfilled') {
        const plan = billingRes.value?.data?.current_plan;
        setCanPost(!!plan?.plAllowCommunity);
      }
    } catch { toast.error('Failed to load posts'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(0, filter); }, [filter]);  // eslint-disable-line

  const changeFilter = (f) => { setFilter(f); setOffset(0); };

  const handlePosted = (pId) => {
    setShowNew(false);
    setView(pId);
  };

  const goPage = (o) => { setOffset(o); load(o, filter); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const startPost = () => (canPost ? setShowNew(true) : navigate('/billing'));
  const closeNew = useCallback(() => setShowNew(false), []);

  const pages = Math.ceil(total / LIMIT);
  const page  = Math.floor(offset / LIMIT) + 1;

  return (
    <MeCtx.Provider value={user?.sId}>
      <div className="cmy">
        {view !== 'list' ? (
          <PostDetail pId={view} canPost={canPost} onBack={() => { setView('list'); load(offset, filter); }} />
        ) : (
          <>
            <header className="cmy-hero">
              <div className="cmy-hero-glow" aria-hidden="true" />
              <div className="cmy-hero-copy">
                <span className="cmy-eyebrow"><i className="fa-solid fa-earth-africa" /> Community</span>
                <h1>Ask, share and solve together</h1>
                <p>Stuck on a command, a flow or a Meta setting? Ask other CommandKit builders — or help someone else get unstuck.</p>
              </div>
              <div className="cmy-hero-side">
                <button type="button" className={`cmy-btn ${canPost ? 'primary' : 'ondark'} lg`} onClick={startPost}
                  title={!canPost ? 'Upgrade your plan to post in the community' : ''}>
                  {canPost ? <><i className="fa-solid fa-plus" /> Ask a question</> : <><i className="fa-solid fa-lock" /> Upgrade to post</>}
                </button>
                <span className="cmy-hero-count"><b>{total.toLocaleString()}</b> {total === 1 ? 'discussion' : 'discussions'}</span>
              </div>
            </header>

            {!canPost && (
              <div className="cmy-banner" role="status">
                <span className="cmy-banner-ic"><i className="fa-solid fa-lock" /></span>
                <div>
                  <b>Read-only on your current plan</b>
                  <span>You can browse every discussion, but posting and replying need a plan with Community access.</span>
                </div>
                <button type="button" className="cmy-btn dark sm" onClick={() => navigate('/billing')}>See plans</button>
              </div>
            )}

            <div className="cmy-layout">
              <section className="cmy-feed">
                <div className="cmy-toolbar">
                  <div className="cmy-seg" role="tablist" aria-label="Sort discussions">
                    {FILTERS.map(f => (
                      <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                        className={filter === f.key ? 'is-active' : ''} onClick={() => changeFilter(f.key)}>
                        <i className={`fa-solid ${f.icon}`} /> {f.label}
                      </button>
                    ))}
                  </div>
                  <span className="cmy-count">{total.toLocaleString()} post{total !== 1 ? 's' : ''}</span>
                </div>

                {loading ? (
                  <div className="cmy-list">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className="cmy-item cmy-skel"><div className="cmy-item-main"><span className="sk w30" /><span className="sk w70 h18" /><span className="sk w90" /><span className="sk w40" /></div></div>
                    ))}
                  </div>
                ) : posts.length === 0 ? (
                  <div className="cmy-empty">
                    <span className="cmy-empty-ic"><i className="fa-regular fa-comments" /></span>
                    <b>{filter === 'open' ? 'Nothing unanswered right now' : 'No discussions yet'}</b>
                    <span>{filter === 'open' ? 'Every question has a reply. Nice work, community.' : 'Start the first one — someone else probably has the same question.'}</span>
                    <button type="button" className="cmy-btn dark" onClick={startPost}>
                      {canPost ? <><i className="fa-solid fa-plus" /> Ask a question</> : 'Upgrade to post'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cmy-list">
                      {posts.map(p => <PostCard key={p.pId} post={p} onClick={() => setView(p.pId)} />)}
                    </div>

                    {pages > 1 && (
                      <div className="cmy-pager">
                        <button type="button" className="cmy-btn ghost" disabled={offset === 0} onClick={() => goPage(Math.max(0, offset - LIMIT))}>
                          <i className="fa-solid fa-chevron-left" /> <span>Previous</span>
                        </button>
                        <span className="cmy-pager-info">Page <b>{page}</b> of {pages}</span>
                        <button type="button" className="cmy-btn ghost" disabled={offset + LIMIT >= total} onClick={() => goPage(offset + LIMIT)}>
                          <span>Next</span> <i className="fa-solid fa-chevron-right" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>

              <aside className="cmy-side">
                <div className="cmy-card cmy-tips">
                  <h4><i className="fa-solid fa-lightbulb" /> Get a faster answer</h4>
                  <ol>
                    <li><b>Say what you expected</b> and what actually happened.</li>
                    <li><b>Paste the error</b> from your bot’s Logs tab.</li>
                    <li><b>Add a screenshot</b> and draw an arrow at the problem.</li>
                    <li><b>Mark the answer</b> that fixed it, so others can find it.</li>
                  </ol>
                </div>
                <div className="cmy-card cmy-side-help">
                  <span className="cmy-side-ic"><i className="fa-solid fa-book-open" /></span>
                  <div>
                    <b>Check the guide first</b>
                    <span>Most setup questions are answered in How to Use.</span>
                  </div>
                  <button type="button" className="cmy-icon-btn" onClick={() => navigate('/how-to-use')} aria-label="Open How to Use">
                    <i className="fa-solid fa-arrow-right" />
                  </button>
                </div>
              </aside>
            </div>
          </>
        )}

        {showNew && <NewPostModal onClose={closeNew} onPosted={handlePosted} />}

        {view === 'list' && canPost && (
          <button type="button" className="cmy-fab" onClick={startPost} aria-label="Ask a question">
            <i className="fa-solid fa-plus" />
          </button>
        )}
      </div>
    </MeCtx.Provider>
  );
}
